import { useState, useContext } from 'react';
import _noop from 'lodash/noop';
import _isNil from 'lodash/isNil';

import { ModalContext, AppContext } from 'commons-ui';
import useService from '../../useService';
import { IUseContentProps } from '../types';
import {
	BLOCK_REASONS,
	BLOCK_UNBLOCK_ADMIN_ACTION_TYPES,
	UPDATE_USER_FRAUD_STATUS_ADMIN_ACTION_TYPES,
	MARK_FRAUD_OPTIONS
} from './constants';

const useContent = (props: IUseContentProps) => {
	const { handleForceUpdate = _noop, adminActionType = BLOCK_UNBLOCK_ADMIN_ACTION_TYPES.UNBLOCK } = props;
	const { blockUnblockUser, updateUserFraudStatus, updateUserLevel } = useService();

	const [showLoader, setShowLoader] = useState(false);
	const { setModalConfig } = useContext(ModalContext);
	const { showAlert } = useContext(AppContext);

	const handleFormSubmit = async (submittedData: any) => {
		let state = 0;
		try {
			setShowLoader(true);
			let data: any = { adminActionType };
			if (adminActionType === BLOCK_UNBLOCK_ADMIN_ACTION_TYPES.BLOCK) {
				const { selectedReason, otherReason, attachments, freshdeskTicketId, markFraud = null } = submittedData;
				const reason = selectedReason === BLOCK_REASONS.OTHER ? otherReason : selectedReason;
				data = { ...data, reason, attachments, freshdeskTicketId, markFraud };
			} else {
				const { reason, attachments, freshdeskTicketId } = submittedData;
				data = { ...data, reason, attachments, freshdeskTicketId };
			}
			await blockUnblockUser(data);
			if (!_isNil(data.markFraud) && data.markFraud === MARK_FRAUD_OPTIONS.YES) {
				state = 1;
				await updateUserFraudStatus({
					...data,
					adminActionType: UPDATE_USER_FRAUD_STATUS_ADMIN_ACTION_TYPES.MARK_USER_FRAUD
				});
			}
			setShowLoader(false);
			handleForceUpdate();
		} catch (error) {
			if (state === 0) {
				showAlert('Error in blocking/unblocking user', { type: 'error' });
			} else if (state === 1) {
				setShowLoader(false);
				handleForceUpdate();
				showAlert('User blocked successfully but error in updating user fraud status', { type: 'error' });
			}
		} finally {
			setModalConfig({ isOpen: false });
		}
	};

	const handleFormCancel = () => {
		setModalConfig({ isOpen: false });
	};

	const handleUpdateUserFraudStatusFormSubmit = async (submittedData: any) => {
		try {
			setShowLoader(true);
			await updateUserFraudStatus({ ...submittedData, adminActionType });
			setShowLoader(false);
			handleForceUpdate();
		} catch (error) {
			showAlert('Error updating user fraud status', { type: 'error' });
		} finally {
			setModalConfig({ isOpen: false });
		}
	};

	const handleUpdateUserFraudStatusFormCancel = () => {
		setModalConfig({ isOpen: false });
	};

	const handleUpdateUserLevelFormSubmit = async (submittedData: any) => {
		try {
			setShowLoader(true);
			await updateUserLevel(submittedData);
			setShowLoader(false);
			handleForceUpdate();
		} catch (error) {
			showAlert('Error updating user level', { type: 'error' });
		} finally {
			setModalConfig({ isOpen: false });
		}
	};

	const handleUpdateUserLevelFormCancel = () => {
		setModalConfig({ isOpen: false });
	};

	return {
		showLoader,
		setShowLoader,
		handleFormCancel,
		handleFormSubmit,
		handleUpdateUserFraudStatusFormSubmit,
		handleUpdateUserFraudStatusFormCancel,
		handleUpdateUserLevelFormSubmit,
		handleUpdateUserLevelFormCancel
	};
};

export default useContent;
